
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  ref,
  toRefs,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  ElMessage,
  FormInstance,
  FormRules,
  UploadFile,
  UploadProps,
} from 'element-plus'
import { INPUT_NAMES } from '@/widgets/WOrganizationEditionForm/constants/inputNames'
import {
  editOrganization,
  getOrganization,
} from '@/widgets/WOrganizationEditionForm/api/organizationEdition.api'
import { CustomTariffData, OrganizationEditType } from '@/shared/types'
import { COMMON_RULES } from '@/shared/constants'
import { tariffsApi } from '@/shared/api/admin/tariffs.api'
import { showErrorMessage } from '@/shared/utils/errors'
import { ORGANIZATION_STATUSES } from '@/shared/constants/organization'

export default defineComponent({
  name: 'WOrganizationEditionForm',

  setup() {
    const route = useRoute()
    const router = useRouter()

    const formInputRefs = reactive({
      nameRef: undefined,
      addressRef: undefined,
      phoneRef: undefined,
      siteRef: undefined,
      innRef: undefined,
      partnerRef: undefined,
      typeRef: undefined,
      isTestRef: undefined,
      isBlockedRef: undefined,
      blockDateRef: undefined,
      packageCountRef: undefined,
      tariffIdRef: undefined,
      subscriptionTypeRef: undefined,
      useCustomTariff: undefined,
      customTariffReportCount: undefined,
      customTariffPriceMonth: undefined,
      customTariffPriceOver: undefined,
    })

    const organizationEditionFormData: OrganizationEditType = reactive({
      name: '',
      address: '',
      phone: '',
      site: '',
      inn: '',
      isTest: undefined,
      isBlocked: undefined,
      partner: null,
      status: null,
      blockDate: '',
      packageCount: undefined,
      logo: [],
      modules: [],
      tariffId: null,
      subscriptionType: null,
      tariff: null,
      useCustomTariff: undefined,
      customTariffData: null,
      dealDate: null,
      lastPaymentDate: null,
      telegramEnabled: null,
      telegramNotificationsEnabled: null,
      qrCodesEnabled: null,
    })

    const tariffList = ref([])
    const defaultTariffId = ref(-1)

    const updateTariffFields = () => {
      if (organizationEditionFormData.tariffId == defaultTariffId.value) {
        if (!organizationEditionFormData.tariff) {
          organizationEditionFormData.useCustomTariff = true
          return
        }

        Object.assign(organizationEditionFormData, {
          customTariffData: {
            price_month: organizationEditionFormData.tariff.price_month,
            price_over: organizationEditionFormData.tariff.price_over,
            report_count: organizationEditionFormData.tariff.report_count,
            price_year: organizationEditionFormData.tariff.price_year,
          },
          useCustomTariff: true,
        })
      } else {
        const newTariff = tariffList.value.find(
          (tariff) => tariff.id === organizationEditionFormData.tariffId
        )

        if (!newTariff) {
          organizationEditionFormData.useCustomTariff = false
          return
        }

        Object.assign(organizationEditionFormData, {
          customTariffData: {
            price_month: newTariff.price_month,
            price_over: newTariff.price_over,
            report_count: newTariff.report_count,
            price_year: newTariff.price_year,
          },
          useCustomTariff: false,
        })
      }
    }

    const organizationEditionValidationRules = reactive<FormRules>({
      name: [COMMON_RULES.organizationNameRequired],
      address: [COMMON_RULES.addressRequired],
      isTest: [
        {
          required: true,
          message: 'Выберите уровень доступа',
          trigger: 'blur',
        },
      ],
      isBlocked: [
        { required: true, message: 'Выберите статус', trigger: 'blur' },
      ],
      blockDate: [{ required: true, message: 'Введите дату', trigger: 'blur' }],
      packageCount: [
        { required: true, message: 'Введите количество', trigger: 'blur' },
      ],
    })

    const organizationEditionForm = ref<FormInstance>()
    const loading = ref(false)

    const organizationId = computed(() => Number(route.params.organizationId))

    const getOrganizationInfo = async (): Promise<void> => {
      loading.value = true

      const organization = await getOrganization(organizationId.value)

      Object.assign(organizationEditionFormData, organization)

      if (organizationEditionFormData.useCustomTariff) {
        defaultTariffId.value = organizationEditionFormData.tariff.id
      } else if (organizationEditionFormData.tariff) {
        Object.assign(
          organizationEditionFormData.customTariffData,
          organizationEditionFormData.tariff
        )
      }

      loading.value = false
    }

    const getTariffs = async (): Promise<void> => {
      const [error, response] = await tariffsApi.getTariffsList()

      if (error) {
        showErrorMessage(error)
      } else {
        tariffList.value = response.data.tariffs
      }
    }

    const handleChangeFocusedInput = (nextInput: string): void => {
      formInputRefs[nextInput].focus()
    }

    const handleExceedImages: UploadProps['onExceed'] = () => {
      ElMessage({
        message: 'Невозможно загрузить больше одного логотипа',
        type: 'warning',
      })
    }

    const handleUploadImage = (file: UploadFile): void => {
      organizationEditionFormData.logo[0] = file
    }

    const handleRemoveImage = (): void => {
      organizationEditionFormData.logo = []
    }

    const handleSubmitForm = (): void => {
      if (!organizationEditionForm.value) return

      organizationEditionForm.value.validate(
        async (valid): Promise<boolean> => {
          if (!valid) return false

          loading.value = true

          const isOrganizationEdited = await editOrganization(
            organizationId.value,
            organizationEditionFormData
          )

          loading.value = false

          if (isOrganizationEdited) {
            router.push({
              name: 'OrganizationStatisticPage',
              params: { organizationId: organizationId.value },
            })
          }
        }
      )
    }

    onMounted((): void => {
      getOrganizationInfo()
      getTariffs()
    })

    return {
      ...toRefs(formInputRefs),
      INPUT_NAMES,
      organizationEditionFormData,
      organizationEditionValidationRules,
      organizationEditionForm,
      loading,
      tariffList,
      defaultTariffId,
      ORGANIZATION_STATUSES,
      updateTariffFields,
      handleChangeFocusedInput,
      handleSubmitForm,
      handleExceedImages,
      handleUploadImage,
      handleRemoveImage,
    }
  },
})
